import React, { useRef, useEffect } from 'react';
import { FiUser, FiMessageSquare } from 'react-icons/fi';
import { BsRobot } from 'react-icons/bs';

const ChatHistory = ({ chatHistory = [], agentMessages = [], userResponses = [], internalNotes = [] }) => {
  const chatEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory, agentMessages, userResponses, internalNotes]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Combine all messages and sort by date
  const combineAndSortMessages = () => {
    const allMessages = [];

    // Add chat history (bot-user conversation)
    if (chatHistory && chatHistory.length > 0) {
      chatHistory.forEach(msg => {
        allMessages.push({
          type: msg.sender === 'bot' ? 'bot' : 'user',
          content: msg.content?.text || msg.message || '',
          timestamp: msg.createdAt || msg.timestamp,
          sender: msg.sender
        });
      });
    }

    // Add agent messages
    if (agentMessages && agentMessages.length > 0) {
      agentMessages.forEach(msg => {
        allMessages.push({
          type: 'agent',
          content: msg.message,
          timestamp: msg.sentAt,
          senderName: msg.sentBy?.name || 'Admin'
        });
      });
    }

    // Add user responses
    if (userResponses && userResponses.length > 0) {
      userResponses.forEach(msg => {
        allMessages.push({
          type: 'user_response',
          content: msg.message,
          timestamp: msg.respondedAt
        });
      });
    }

    // Add internal notes
    if (internalNotes && internalNotes.length > 0) {
      internalNotes.forEach(note => {
        allMessages.push({
          type: 'internal_note',
          content: note.note,
          timestamp: note.addedAt,
          senderName: note.addedBy?.name || 'Admin'
        });
      });
    }

    // Sort by timestamp
    return allMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const allMessages = combineAndSortMessages();

  // Get message style based on type
  const getMessageStyle = (type) => {
    switch (type) {
      case 'bot':
        return 'bg-gray-100 text-gray-800';
      case 'user':
      case 'user_response':
        return 'bg-blue-500 text-white';
      case 'agent':
        return 'bg-green-500 text-white';
      case 'internal_note':
        return 'bg-yellow-50 text-yellow-800 border border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get message alignment
  const getMessageAlignment = (type) => {
    switch (type) {
      case 'user':
      case 'user_response':
        return 'justify-end';
      case 'agent':
        return 'justify-end';
      case 'internal_note':
        return 'justify-center';
      default:
        return 'justify-start';
    }
  };

  // Get sender icon
  const getSenderIcon = (type) => {
    switch (type) {
      case 'bot':
        return <BsRobot className="text-gray-500" size={14} />;
      case 'user':
      case 'user_response':
        return <FiUser className="text-blue-200" size={14} />;
      case 'agent':
        return <FiMessageSquare className="text-green-200" size={14} />;
      default:
        return null;
    }
  };

  // Get sender label
  const getSenderLabel = (msg) => {
    switch (msg.type) {
      case 'bot':
        return 'Chatbot';
      case 'user':
        return 'User';
      case 'user_response':
        return 'User Reply';
      case 'agent':
        return msg.senderName || 'Admin';
      case 'internal_note':
        return `Internal Note - ${msg.senderName || 'Admin'}`;
      default:
        return '';
    }
  };

  if (allMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500">
        <div className="text-center">
          <FiMessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
          <p>No conversation history</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-96 overflow-y-auto p-4 bg-gray-50 rounded-lg">
      {allMessages.map((msg, index) => (
        <div
          key={index}
          className={`flex ${getMessageAlignment(msg.type)}`}
        >
          <div
            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${getMessageStyle(msg.type)} ${
              msg.type === 'internal_note' ? 'w-full max-w-none' : ''
            }`}
          >
            {/* Sender label */}
            <div className={`flex items-center gap-1 text-xs mb-1 ${
              msg.type === 'user' || msg.type === 'user_response' || msg.type === 'agent'
                ? 'text-white/70'
                : msg.type === 'internal_note'
                  ? 'text-yellow-600 font-medium'
                  : 'text-gray-500'
            }`}>
              {getSenderIcon(msg.type)}
              <span>{getSenderLabel(msg)}</span>
            </div>

            {/* Message content */}
            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>

            {/* Timestamp */}
            <p className={`text-xs mt-1 ${
              msg.type === 'user' || msg.type === 'user_response' || msg.type === 'agent'
                ? 'text-white/60'
                : msg.type === 'internal_note'
                  ? 'text-yellow-500'
                  : 'text-gray-400'
            }`}>
              {formatDate(msg.timestamp)}
            </p>
          </div>
        </div>
      ))}
      <div ref={chatEndRef} />
    </div>
  );
};

export default ChatHistory;
